// A pan/zoom camera mapping world coordinates to screen pixels.
//   screen = (world - offset) * zoom
//
// The camera keeps TWO states: the one being drawn (`offX/offY/zoom`) and the
// one being steered toward (`tOffX/tOffY/tZoom`). `update()` eases the first
// into the second every frame, which is what turns a wheel notch from a jump
// into a glide.
//
// Dragging is deliberately NOT eased: a pan applies to both states at once, so
// the world stays glued to the finger. Only zoom — where the input is discrete
// and the motion is large — gets smoothed. Easing a direct manipulation feels
// like lag; easing a discrete one feels like polish.
//
// `update()` is called by the Renderer, not by the app, so nothing outside has
// to know the camera animates.

import type { Vec2 } from "../sim/vec2";

/** Fraction of the remaining distance closed per 60 Hz frame. */
const EASE_PER_FRAME = 0.26;
const FRAME_MS = 1000 / 60;

export class Camera {
  // Drawn state.
  offX = 0;
  offY = 0;
  zoom = 0.6;

  // Steered-toward state.
  tOffX = 0;
  tOffY = 0;
  tZoom = 0.6;

  minZoom = 0.05;
  maxZoom = 24;

  private lastUpdate = 0;

  // --- Projection (always uses the DRAWN state) -----------------------------

  worldToScreen(wx: number, wy: number, out: Vec2): Vec2 {
    out.x = (wx - this.offX) * this.zoom;
    out.y = (wy - this.offY) * this.zoom;
    return out;
  }

  screenToWorld(sx: number, sy: number, out: Vec2): Vec2 {
    out.x = sx / this.zoom + this.offX;
    out.y = sy / this.zoom + this.offY;
    return out;
  }

  // --- Input ----------------------------------------------------------------

  /** Drag. Applied to both states so the grab point never slips. */
  pan(dxScreen: number, dyScreen: number): void {
    const dx = dxScreen / this.zoom;
    const dy = dyScreen / this.zoom;
    this.offX -= dx;
    this.offY -= dy;
    this.tOffX -= dx;
    this.tOffY -= dy;
  }

  /**
   * Zoom by factor `f` while keeping the world point under (sx,sy) fixed.
   * Anchored on the TARGET so a fast burst of wheel notches composes correctly
   * instead of each one re-anchoring against a half-finished animation.
   */
  zoomAt(sx: number, sy: number, f: number): void {
    const wx = sx / this.tZoom + this.tOffX;
    const wy = sy / this.tZoom + this.tOffY;
    this.tZoom = Math.min(this.maxZoom, Math.max(this.minZoom, this.tZoom * f));
    this.tOffX = wx - sx / this.tZoom;
    this.tOffY = wy - sy / this.tZoom;
  }

  /** Ease the drawn state toward the target. Called once per rendered frame. */
  update(nowMs: number): void {
    let dt = this.lastUpdate > 0 ? nowMs - this.lastUpdate : FRAME_MS;
    this.lastUpdate = nowMs;
    if (dt <= 0) return;
    if (dt > 100) dt = 100; // a tab that was backgrounded shouldn't snap-warp

    // Frame-rate independent exponential approach: the same visual speed at
    // 30 fps and 144 fps.
    const k = 1 - Math.pow(1 - EASE_PER_FRAME, dt / FRAME_MS);

    // Zoom eases GEOMETRICALLY. Linear interpolation of a scale factor
    // decelerates unevenly — 1→4 and 4→16 should feel like the same gesture.
    const ratio = this.tZoom / this.zoom;
    if (ratio > 1.0002 || ratio < 0.9998) {
      this.zoom *= Math.pow(ratio, k);
    } else {
      this.zoom = this.tZoom;
    }

    const dx = this.tOffX - this.offX;
    const dy = this.tOffY - this.offY;
    const snap = 0.35 / this.zoom; // sub-pixel: stop chasing
    this.offX = Math.abs(dx) < snap ? this.tOffX : this.offX + dx * k;
    this.offY = Math.abs(dy) < snap ? this.tOffY : this.offY + dy * k;
  }

  // --- Framing --------------------------------------------------------------

  /** Fit a world of the given size into a viewport, centered. Instant. */
  fit(worldW: number, worldH: number, viewW: number, viewH: number): void {
    if (viewW <= 0 || viewH <= 0) return;
    this.zoom = Math.min(viewW / worldW, viewH / worldH) * 0.95;
    this.offX = worldW / 2 - viewW / 2 / this.zoom;
    this.offY = worldH / 2 - viewH / 2 / this.zoom;
    this.snap();
  }

  /** Glide so that a world point sits at the centre of the viewport. */
  centerOn(wx: number, wy: number, viewW: number, viewH: number): void {
    this.tOffX = wx - viewW / 2 / this.tZoom;
    this.tOffY = wy - viewH / 2 / this.tZoom;
  }

  /** Glide to an absolute zoom level about the viewport centre. */
  zoomTo(z: number, viewW: number, viewH: number): void {
    this.zoomAt(viewW / 2, viewH / 2, z / this.tZoom);
  }

  /** Collapse the animation — target becomes what is drawn. */
  snap(): void {
    this.tOffX = this.offX;
    this.tOffY = this.offY;
    this.tZoom = this.zoom;
    this.lastUpdate = 0;
  }

  /**
   * Keep the world within reach. Without this you can pan into empty space
   * forever and have no way back; the allowance is generous (you may push the
   * map most of the way off screen) so it never fights a deliberate drag.
   */
  clampTo(worldW: number, worldH: number, viewW: number, viewH: number): void {
    this.tZoom = Math.min(this.maxZoom, Math.max(this.minZoom, this.tZoom));
    this.zoom = Math.min(this.maxZoom, Math.max(this.minZoom, this.zoom));
    this.tOffX = axisClamp(this.tOffX, viewW, worldW, this.tZoom);
    this.tOffY = axisClamp(this.tOffY, viewH, worldH, this.tZoom);
    this.offX = axisClamp(this.offX, viewW, worldW, this.zoom);
    this.offY = axisClamp(this.offY, viewH, worldH, this.zoom);
  }
}

function axisClamp(off: number, viewSpan: number, worldSpan: number, zoom: number): number {
  const view = viewSpan / zoom; // world units currently visible on this axis
  const margin = view * 0.45; // how far past the edge you may push
  let lo = -margin;
  let hi = worldSpan - view + margin;
  if (lo > hi) {
    // World is smaller than the view (zoomed way out): centre it.
    lo = hi = (worldSpan - view) / 2;
  }
  return off < lo ? lo : off > hi ? hi : off;
}
